import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaPaperPlane,
  FaCheckCircle,
  FaFacebookF,
  FaInstagram,
  FaWhatsapp
} from 'react-icons/fa';
import PageHero from '../components/UI/PageHero';
import { COMPANY, IMG } from '../data/siteData';

const TRANSPORT_TYPES = [
  'Truck Transport',
  'Lorry Transport',
  'Goods Delivery',
  'Industrial Transport',
  'Construction Material',
  'Sand & Metal',
  'Long Distance Freight',
  'Commercial Logistics'
];

const WHATSAPP_NUMBER = '918078103460';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    transportType: '',
    message: ''
  });

  const validateForm = (data) => {
    const newErrors = {};

    // Name
    if (!data.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (data.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[A-Za-z\s.'-]+$/.test(data.name.trim())) {
      newErrors.name = 'Name can contain only letters';
    }

    // Phone
    if (!data.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(data.phone.trim())) {
      newErrors.phone = 'Enter a valid 10-digit mobile number';
    }

    // Email
    if (!data.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
        data.email.trim()
      )
    ) {
      newErrors.email = 'Enter a valid email address';
    }

    // Location
    if (!data.location.trim()) {
      newErrors.location = 'Location is required';
    } else if (data.location.trim().length < 2) {
      newErrors.location = 'Please enter a valid location';
    } else if (!/^[A-Za-z0-9\s,.'-]+$/.test(data.location.trim())) {
      newErrors.location = 'Location contains invalid characters';
    }

    // Transport Type
    if (!data.transportType) {
      newErrors.transportType = 'Please select a transport type';
    }

    // Message
    if (!data.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (data.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (data.message.trim().length > 500) {
      newErrors.message = 'Message cannot exceed 500 characters';
    }

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const data = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      location: formData.location,
      transportType: formData.transportType,
      message: formData.message
    };

    const validationErrors = validateForm(data);
    setErrors(validationErrors);

    // Stop submission if validation fails
    if (Object.keys(validationErrors).length > 0) {
      console.log('Validation errors:', validationErrors);
      // Scroll to first error
      const firstErrorField = document.querySelector('.form-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // If validation passes, send to WhatsApp
    const whatsappMessage = `
*NEW TRANSPORT ENQUIRY*

*Name:* ${data.name.trim()}
*Phone:* ${data.phone.trim()}
*Email:* ${data.email.trim()}
*Location:* ${data.location.trim()}
*Transport Type:* ${data.transportType}

*Message:*
${data.message.trim()}
    `.trim();

    const whatsappURL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      whatsappMessage
    )}`;

    console.log('Opening WhatsApp with URL:', whatsappURL);
    window.open(whatsappURL, '_blank');

    // Show success message and reset form
    setSubmitted(true);
    setFormData({
      name: '',
      phone: '',
      email: '',
      location: '',
      transportType: '',
      message: ''
    });
    setErrors({});
  };

  return (
    <>
      <PageHero
        eyebrow="Get In Touch"
        title="Book Your Transport"
        subtitle="Reach our dispatch desk for a quote, a scheduled pickup, or an urgent haul — we answer around the clock."
        bg={IMG.aerialSand}
      />

      <section className="section contact-page">
        <div className="container contact-page__grid">

          {/* LEFT SIDE */}
          <motion.div
            className="contact-page__media"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={IMG.nightStreet}
              alt="Ambadykannan Transport truck ready for dispatch"
              loading="lazy"
            />

            <div className="contact-page__info glass">

              <div>
                <FaPhoneAlt />
                <div>
                  <h4>Phone</h4>
                  <p>{COMPANY.phone}</p>
                </div>
              </div>

              <div>
                <FaEnvelope />
                <div>
                  <h4>Email</h4>
                  <p>{COMPANY.email}</p>
                </div>
              </div>

              <div>
                <FaMapMarkerAlt />
                <div>
                  <h4>Address</h4>
                  <p>{COMPANY.address}</p>
                </div>
              </div>

              <div>
                <FaClock />
                <div>
                  <h4>Business Hours</h4>
                  <p>{COMPANY.hours}</p>
                </div>
              </div>

            </div>

            {/* <div className="contact-page__socials">

              <a href="#" aria-label="Facebook">
                <FaFacebookF />
              </a>

              <a href="#" aria-label="Instagram">
                <FaInstagram />
              </a>

              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
              >
                <FaWhatsapp />
              </a>

            </div> */}
          </motion.div>

          {/* RIGHT SIDE */}
          <motion.div
            className="contact-page__form-wrap glass"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
          >

            {submitted ? (
              <div className="contact-page__success">

                <FaCheckCircle />

                <h3>Enquiry Sent</h3>

                <p>
                  Thanks for reaching out. Your enquiry has been
                  sent to our WhatsApp. Our dispatch team will
                  contact you shortly.
                </p>

                <button
                  className="btn btn-primary"
                  onClick={() => setSubmitted(false)}
                >
                  Send Another
                </button>

              </div>
            ) : (
              <>
                <p className="eyebrow">Send An Enquiry</p>

                <h2 className="section-title contact-page__title">
                  Talk To Dispatch
                </h2>

                <form
                  className="contact-page__form"
                  onSubmit={handleSubmit}
                  noValidate
                >

                  {/* NAME + PHONE */}
                  <div className="contact-page__row">

                    <div className="form-group">
                      <input
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        maxLength={50}
                        autoComplete="name"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^A-Za-z\s.'-]/g,
                            ''
                          );
                        }}
                      />

                      {errors.name && (
                        <span className="form-error">
                          {errors.name}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        maxLength={10}
                        inputMode="numeric"
                        autoComplete="tel"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(
                            /\D/g,
                            ''
                          );
                        }}
                      />

                      {errors.phone && (
                        <span className="form-error">
                          {errors.phone}
                        </span>
                      )}
                    </div>

                  </div>

                  {/* EMAIL + LOCATION */}
                  <div className="contact-page__row">

                    <div className="form-group">
                      <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        maxLength={100}
                        autoComplete="email"
                      />

                      {errors.email && (
                        <span className="form-error">
                          {errors.email}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <input
                        type="text"
                        name="location"
                        placeholder="Location"
                        value={formData.location}
                        onChange={handleInputChange}
                        maxLength={100}
                        autoComplete="address-level2"
                      />

                      {errors.location && (
                        <span className="form-error">
                          {errors.location}
                        </span>
                      )}
                    </div>

                  </div>

                  {/* TRANSPORT TYPE */}
                  <div className="form-group">

                    <select
                      name="transportType"
                      value={formData.transportType}
                      onChange={handleInputChange}
                    >
                      <option value="" disabled>
                        Transport Type
                      </option>

                      {TRANSPORT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>

                    {errors.transportType && (
                      <span className="form-error">
                        {errors.transportType}
                      </span>
                    )}

                  </div>

                  {/* MESSAGE */}
                  <div className="form-group">

                    <textarea
                      name="message"
                      placeholder="Message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                    />

                    {errors.message && (
                      <span className="form-error">
                        {errors.message}
                      </span>
                    )}

                    <div className="message-info">
                      <span>{formData.message.length}/500 characters</span>
                    </div>

                  </div>

                  {/* SUBMIT */}
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Send Enquiry <FaPaperPlane />
                  </button>

                </form>
              </>
            )}

          </motion.div>

        </div>

        {/* MAP */}
        <motion.div
          className="container contact-page__map-wrap"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
        >

          <iframe title="Ambadykannan Transport location map"
            className="contact-page__map" src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3726.021926322045!2d76.7201884750413!3d10.736083889410288!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTDCsDQ0JzA5LjkiTiA3NsKwNDMnMjIuMCJF!5e1!3m2!1sen!2sin!4v1786431407614!5m2!1sen!2sin" width="600" height="450" style={{border:0}} allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>
        </motion.div>

      </section>
    </>
  );
}